import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Auth } from '../../services/Contexture/Auth';
import { Contexture } from '../../services/Contexture/Contexture';

const parseProfiles = (profiles) => {
  let parsedProfiles = {};

  profiles.forEach((p, i) => {
    let key = null;
    if (p.profileName === 'Cybersecurity News') {
      key = 'news';
    } else if (p.profileName === 'NIST NVD Postings') {
      key = 'cve';
    } else if (p.profileName.includes('Toolbox News')) {
      key = 'tools_news';
    } else if (p.profileName.includes('Toolbox NIST')) {
      key = 'tools_cve';
    } else if (p.profileName.includes('Stack News')) {
      key = 'stack_news';
    } else if (p.profileName.includes('Stack NIST')) {
      key = 'stack_cve';
    } else if (p.profileName === 'Cybersecurity Podcasts') {
      key = 'podcasts';
    } else if (p.profileName === 'MITRE ATT&CK') {
      key = 'mitre';
    } else if (
      p.profileName.includes('Podcasts') &&
      p.profileName.includes('Tools')
    ) {
      key = 'tools_podcasts';
    } else if (p.tags && p.tags.indexOf('Log4J')) {
      if (p.profileName.includes('News')) {
        key = 'wksp_log4j_news';
      } else if (p.profileName.includes('MITRE')) {
        key = 'wksp_log4j_mitre';
      } else if (p.profileName.includes('Podcasts')) {
        key = 'wksp_log4j_podcasts';
      } else if (p.profileName.includes('CVE')) {
        key = 'wksp_log4j_cve';
      }
    } else if (
      p.profileName.includes('Podcasts') &&
      p.profileName.includes('Stack')
    ) {
      key = 'stack_podcasts';
    } else {
      key = 'extra-' + i;
    }

    parsedProfiles = { ...parsedProfiles, [key]: p };
  });

  return parsedProfiles;
};

const initialState = {
  isFetching: false,
  user: null,
  profiles: null,
  token: null,
  onboardStep: 0,
  isTeamOwner: false,
  error: null,
};

export const authenticateWithContexture = createAsyncThunk(
  'auth/authenticateWithContexture',
  async (oidcUser, { rejectWithValue }) => {
    try {
      const profile = oidcUser?.profile || {};
      const fallbackUsername =
        profile['cognito:username'] || profile.sub || null;
      const authCredentials = {
        email:
          profile.email ||
          (fallbackUsername ? `${fallbackUsername}@placeholder.local` : null),
        organization: 'Ecropolis',
        given_name: profile.given_name || profile.name || 'User',
        family_name: profile.family_name || '',
      };

      if (!authCredentials.email) {
        return rejectWithValue('Missing email-like identifier for authentication');
      }

      const { token } = await Contexture.authenticate(authCredentials);

      if (!token) {
        return rejectWithValue('No token returned from Contexture.authenticate');
      }

      localStorage.setItem('contexture_auth', JSON.stringify(authCredentials));

      const userInfo = await Auth.getUserInfo();

      if (!userInfo) {
        return rejectWithValue('Unable to fetch user info');
      }

      const userWithProfile = {
        ...userInfo,
        profile: oidcUser?.profile || {},
      };

      const profiles = parseProfiles(userInfo.profiles || []);

      Contexture.setToken(token);
      Contexture.setProfiles(profiles);

      const isTeamOwner = userInfo.roles?.includes('team_owner');

      return {
        token,
        user: userWithProfile,
        onboardStep: userInfo.onboardStep || 0,
        isTeamOwner: !!isTeamOwner,
        profiles,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to authenticate with Contexture');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthState(state) {
      Object.assign(state, initialState);
      localStorage.removeItem('contexture_auth');
      Contexture.setToken(null);
      Contexture.setProfiles(null);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authenticateWithContexture.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(authenticateWithContexture.fulfilled, (state, action) => {
        state.isFetching = false;
        state.error = null;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.onboardStep = action.payload.onboardStep;
        state.isTeamOwner = action.payload.isTeamOwner;
        state.profiles = action.payload.profiles;
      })
      .addCase(authenticateWithContexture.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload || action.error.message;
        state.token = null;
        state.user = null;
        state.profiles = null;
      });
  },
});

export const { clearAuthState } = authSlice.actions;

export default authSlice.reducer;
